import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";

import { appAudioFocus, type AppAudioLease } from "@/lib/appAudioFocus";

export type ActivityType = "baby_feeding" | "baby_sleep" | "focus" | "sleep" | "meditation" | "stimulation";
export type MediaType = "sounds" | "language" | "meditation_audio";

export interface ActiveActivity {
  type: ActivityType;
  label: string;
  emoji: string;
  startedAt: number;
  meta?: Record<string, string>;
}

export interface ActiveMedia {
  type: MediaType;
  label: string;
  emoji: string;
  route: string;
  audioRef?: HTMLAudioElement;
}

interface AppActivityContextType {
  activeActivity: ActiveActivity | null;
  activitySeconds: number;
  startActivity: (activity: Omit<ActiveActivity, "startedAt">) => void;
  stopActivity: () => (ActiveActivity & { seconds: number }) | null;
  isActivityActive: (type: ActivityType) => boolean;
  activeMedia: ActiveMedia | null;
  startMedia: (media: ActiveMedia) => void;
  stopMedia: () => void;
  isMediaActive: (type: MediaType) => boolean;
}

const AppActivityContext = createContext<AppActivityContextType>({
  activeActivity: null,
  activitySeconds: 0,
  startActivity: () => {},
  stopActivity: () => null,
  isActivityActive: () => false,
  activeMedia: null,
  startMedia: () => {},
  stopMedia: () => {},
  isMediaActive: () => false,
});

export const useAppActivity = () => useContext(AppActivityContext);

export const AppActivityProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeActivity, setActiveActivity] = useState<ActiveActivity | null>(null);
  const [activitySeconds, setActivitySeconds] = useState(0);
  const [activeMedia, setActiveMedia] = useState<ActiveMedia | null>(null);
  const mediaFocusRef = useRef<AppAudioLease | null>(null);
  useEffect(() => () => { mediaFocusRef.current?.release(); }, []);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (activeActivity) {
      intervalRef.current = setInterval(() => {
        setActivitySeconds(Math.floor((Date.now() - activeActivity.startedAt) / 1000));
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setActivitySeconds(0);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [activeActivity]);

  const startActivity = useCallback((activity: Omit<ActiveActivity, "startedAt">) => {
    setActiveActivity({ ...activity, startedAt: Date.now() });
    setActivitySeconds(0);
  }, []);

  const stopActivity = useCallback(() => {
    if (!activeActivity) return null;
    const result = { ...activeActivity, seconds: activitySeconds };
    setActiveActivity(null);
    return result;
  }, [activeActivity, activitySeconds]);

  const isActivityActive = useCallback((type: ActivityType) => {
    return activeActivity?.type === type;
  }, [activeActivity]);

  const startMedia = useCallback((media: ActiveMedia) => {
    const previousFocus = mediaFocusRef.current;
    mediaFocusRef.current = appAudioFocus.acquire("media");
    previousFocus?.release();
    if (activeMedia?.audioRef) {
      activeMedia.audioRef.pause();
    }
    setActiveMedia(media);
  }, [activeMedia]);

  const stopMedia = useCallback(() => {
    mediaFocusRef.current?.release();
    mediaFocusRef.current = null;
    if (activeMedia?.audioRef) {
      activeMedia.audioRef.pause();
    }
    setActiveMedia(null);
  }, [activeMedia]);

  const isMediaActive = useCallback((type: MediaType) => {
    return activeMedia?.type === type;
  }, [activeMedia]);

  return (
    <AppActivityContext.Provider value={{
      activeActivity, activitySeconds, startActivity, stopActivity, isActivityActive,
      activeMedia, startMedia, stopMedia, isMediaActive,
    }}>
      {children}
    </AppActivityContext.Provider>
  );
};

export default AppActivityContext;
